import { useSuggestionSearchBarContext } from '@aibees/elephant/feature/suggestion-search-bar/context';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Tooltip, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo, MouseEvent } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { LookalikeButtonProps as Props } from './types';
import { useLookalikeButtonState } from './use-lookalike-button-state';

export const LookalikeButton = memo(({ className, item, sx }: Props) => {
  const { selectedItems, onAddLookalike, onRemoveLookalike, limitReached } =
    useSuggestionSearchBarContext();
  const isSelected = !!selectedItems.find((x) => x.id === item.id);
  const { isDisabled, tooltipText, tooltipTextLimitReached } =
    useLookalikeButtonState(item);

  const handleClick = (e: MouseEvent<HTMLLIElement>) => {
    if (limitReached && !isSelected) return;
    if (isSelected) {
      onRemoveLookalike(item!);
      return;
    }
    onAddLookalike(item!);
  };

  const button = (
    <Styled.Wrapper
      className={classnames('LookalikeButton', className, {
        limitReached: limitReached && !isSelected,
      })}
      sx={mergeSx(SX.root(isSelected), sx)}
      onClick={(e) => handleClick(e as any)}
      disabled={isDisabled}
      size="small"
      color={isSelected ? 'primary' : 'inherit'}
      variant="outlined"
      startIcon={
        <Icon
          name={isDisabled ? 'error' : isSelected ? 'minus' : 'plus'}
          size={18}
        />
      }
    >
      Lookalike
    </Styled.Wrapper>
  );

  return isDisabled ? (
    <Tooltip
      title={
        <Typography
          variant="body4"
          fontWeight={600}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {tooltipText}
        </Typography>
      }
      placement="bottom"
    >
      <span>{button}</span>
    </Tooltip>
  ) : (
    <Tooltip
      title={
        limitReached && !isSelected ? (
          <Box textAlign="center">
            <Typography
              variant="body4"
              fontWeight={600}
              sx={{ whiteSpace: 'pre-line' }}
            >
              {tooltipTextLimitReached}
            </Typography>{' '}
          </Box>
        ) : null
      }
      placement="bottom"
    >
      {button}
    </Tooltip>
  );
});

LookalikeButton.displayName = 'LookalikeButton';
